import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useState } from 'react'

export default function Contact() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setFormStatus('submitting')

    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success')
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      })

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus('idle')
      }, 5000)
    }, 1500)
  }

  const contactIcons = {
    phone: (
      <svg className="w-12 h-12 text-[#E2725B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    email: (
      <svg className="w-12 h-12 text-[#E2725B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    location: (
      <svg className="w-12 h-12 text-[#E2725B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    clock: (
      <svg className="w-12 h-12 text-[#E2725B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }

  const contactInfo = [
    {
      id: 1,
      icon: contactIcons.email,
      label: t('contact.info.email.label'),
      value: t('contact.info.email.value'),
      description: t('contact.info.email.description'),
      alt: t('contact.info.email.iconAlt')
    },
    {
      id: 2,
      icon: contactIcons.location,
      label: t('contact.info.location.label'),
      value: t('contact.info.location.value'),
      description: t('contact.info.location.description'),
      alt: t('contact.info.location.iconAlt')
    },
    {
      id: 3,
      icon: contactIcons.clock,
      label: t('contact.info.hours.label'),
      value: t('contact.info.hours.weekdays'),
      description: t('contact.info.hours.weekdaysTime'),
      alt: t('contact.info.hours.iconAlt')
    }
  ]

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section
        className="relative min-h-[400px] md:min-h-[500px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(226, 114, 91, 0.85), rgba(226, 114, 91, 0.85)), url('https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/Gemini_Generated_Image_r4bkbvr4bkbvr4bk%20(1).png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t('contact.hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-12 md:py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-[#FFDB58]/10 to-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-lg border-2 border-[#FFDB58]/20">
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#333333] mb-3">
                  {t('contact.form.title')}
                </h2>
                <p className="text-base md:text-lg text-[#333333]/70">
                  {t('contact.form.subtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#333333] mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t('contact.form.namePlaceholder')}
                    className="w-full h-12 px-4 rounded-lg border-2 border-[#FFDB58]/30 focus:border-[#E2725B] focus:outline-none transition-colors text-[#333333] placeholder-[#333333]/40"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#333333] mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t('contact.form.emailPlaceholder')}
                    className="w-full h-12 px-4 rounded-lg border-2 border-[#FFDB58]/30 focus:border-[#E2725B] focus:outline-none transition-colors text-[#333333] placeholder-[#333333]/40"
                  />
                </div>

                {/* Phone Field */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#333333] mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('contact.form.phonePlaceholder')}
                    className="w-full h-12 px-4 rounded-lg border-2 border-[#FFDB58]/30 focus:border-[#E2725B] focus:outline-none transition-colors text-[#333333] placeholder-[#333333]/40"
                  />
                </div>

                {/* Subject Field */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-[#333333] mb-2">
                    {t('contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder={t('contact.form.subjectPlaceholder')}
                    className="w-full h-12 px-4 rounded-lg border-2 border-[#FFDB58]/30 focus:border-[#E2725B] focus:outline-none transition-colors text-[#333333] placeholder-[#333333]/40"
                  />
                </div>

                {/* Message Field */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#333333] mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder={t('contact.form.messagePlaceholder')}
                    className="w-full px-4 py-3 rounded-lg border-2 border-[#FFDB58]/30 focus:border-[#E2725B] focus:outline-none transition-colors text-[#333333] placeholder-[#333333]/40 resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full h-12 bg-[#E2725B] hover:bg-[#d65f48] disabled:bg-[#E2725B]/50 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                >
                  {formStatus === 'submitting' ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('contact.form.submitting')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {t('contact.form.submit')}
                    </>
                  )}
                </button>

                {/* Success/Error Messages */}
                {formStatus === 'success' && (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg flex items-center gap-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-green-700 font-medium">{t('contact.form.success')}</p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                    <p className="text-red-700 font-medium">{t('contact.form.error')}</p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#333333] mb-3">
                  {t('contact.info.title')}
                </h2>
                <p className="text-base md:text-lg text-[#333333]/70">
                  {t('contact.info.subtitle')}
                </p>
              </div>

              <div className="space-y-6">
                {contactInfo.map((info) => (
                  <div
                    key={info.id}
                    className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-[#E2725B]/20 hover:border-[#E2725B]/40"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-[#333333] mb-2">
                          {info.label}
                        </h3>
                        {info.id === 2 ? (
                          <>
                            <p className="text-base md:text-lg text-[#E2725B] font-semibold mb-1">
                              {t('contact.info.location.city')}
                            </p>
                            <p className="text-sm text-[#333333]/60">
                              {info.description}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base md:text-lg text-[#E2725B] font-semibold mb-1">
                              {info.value}
                            </p>
                            <p className="text-sm text-[#333333]/60">
                              {info.description}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Links */}
              <div className="mt-8 p-6 bg-gradient-to-br from-[#E2725B]/10 to-transparent rounded-xl border-2 border-[#E2725B]/20">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-8 h-8 text-[#E2725B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <h3 className="text-lg font-bold text-[#333333]">
                    {t('contact.quickTip.title')}
                  </h3>
                </div>
                <p className="text-base text-[#333333]/70 mb-4">
                  {t('contact.quickTip.description')}
                </p>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-2 text-[#E2725B] font-semibold hover:text-[#d65f48] transition-colors"
                >
                  <span>{t('contact.quickTip.button')}</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 lg:py-28 bg-gradient-to-r from-[#E2725B] to-[#E2725B]/90">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('contact.cta.title')}
            </h2>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('contact.cta.subtitle')}
            </p>

            <Link
              to="/products"
              className="inline-flex items-center gap-2 h-12 px-8 bg-white text-[#E2725B] hover:bg-[#FFDB58] hover:text-[#333333] font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t('contact.cta.button')}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            {/* Trust Badges */}
            <div className="mt-12 flex flex-wrap justify-center items-center gap-6 md:gap-8">
              <div className="flex items-center gap-2 text-white/90">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm md:text-base font-medium">24-Hour Response</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm md:text-base font-medium">100% Secure</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm md:text-base font-medium">Expert Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
